import { useHead, useRoute, useRuntimeConfig, useSeoMeta } from "#imports";

// ============================================================================
// Constants for default structured data
// ============================================================================

const DEFAULT_ORG_NAME = "Spring Green";
const DEFAULT_ORG_ALTERNATE_NAME = "Spring Green Lawn Care";
const DEFAULT_ORG_DESCRIPTION = "Professional lawn care, pest control and tree care services.";
const DEFAULT_SITE_URL = "https://www.spring-green.com";

// Typed options for building SEO head data.
// All data must be pre-fetched before calling useSeoHead (e.g., via useAsyncData).
export interface SeoHeadOptions {
  title?: string;
  description?: string;
  robotsIndex?: string;
  robotsFollow?: string;
  openGraph?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    type?: string;
  };
  twitter?: {
    title?: string;
    description?: string;
    image?: string;
    card?: string;
  };
  canonicalPath?: string;
  canonicalHostOverride?: string;
  structuredData?: any;
  /**
   * Breadcrumb trail for the current page.
   * Each entry should include `name` (breadcrumb label) and `url` (absolute URL).
   * When provided, a BreadcrumbList JSON-LD schema is automatically built and
   * merged into the page's structured data.
   */
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Optional @id for the emitted BreadcrumbList node (e.g. "https://…/about-us/#breadcrumb").
   * Only used when `breadcrumbs` is provided.
   */
  breadcrumbId?: string;
  /**
   * Include WebSite schema. Recommended for homepage only.
   * Uses default site info unless websiteData is provided.
   */
  includeWebSite?: boolean;
  /**
   * Custom WebSite schema data to override defaults.
   */
  websiteData?: WebSiteSchemaOptions;
  /**
   * Pre-fetched Organization structured data.
   */
  orgData?: any;
  /**
   * Pre-fetched Aggregate Review structured data.
   */
  aggReviewData?: any;
  /**
   * Pre-fetched LocalBusiness structured data.
   */
  localBusinessData?: any;
  /**
   * Pre-fetched Review structured data array.
   */
  reviews?: any[];
  extraMeta?: Record<string, any>;
}

function buildRobots(routePath: string, robotsIndex?: string, robotsFollow?: string) {
  const restricted = ["/auth", "/portal", "/checkout", "/account"]; // protect paths
  const isRestricted = restricted.some((path) => routePath === path || routePath.startsWith(`${path}/`));

  const index = robotsIndex ?? (isRestricted ? "noindex" : "index");
  const follow = robotsFollow ?? (isRestricted ? "nofollow" : "follow");
  return `${index},${follow}`;
}

function normalizeStructuredData(input: any): any[] {
  const collected: any[] = [];
  const pushItem = (item: any) => {
    if (!item) return;
    if (typeof item === "string") {
      try {
        const parsed = JSON.parse(item);
        pushItem(parsed);
      } catch (e) {
        console.error("Invalid structured data JSON", item, e);
      }
      return;
    }
    if (Array.isArray(item)) {
      item.forEach(pushItem);
      return;
    }
    collected.push(item);
  };

  pushItem(input);
  return collected;
}

export function useSeoHead(options: SeoHeadOptions = {}) {
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();

  const robots = buildRobots(route.path, options.robotsIndex, options.robotsFollow);

  // Determine frontend URL for WebSite schema and OpenGraph
  const frontEndUrl = (runtimeConfig.public?.frontEndUrl || DEFAULT_SITE_URL).replace(/\/$/, "");

  const seoMeta: Record<string, any> = {
    ...(options.title ? { title: options.title } : {}),
    ...(options.description ? { description: options.description } : {}),
    robots,
    ...(options.openGraph?.title ? { ogTitle: options.openGraph.title } : {}),
    ...(options.openGraph?.description ? { ogDescription: options.openGraph.description } : {}),
    ...(options.openGraph?.image ? { ogImage: options.openGraph.image } : {}),
    ...(options.openGraph?.url ? { ogUrl: options.openGraph.url } : {}),
    ...(options.openGraph?.type ? { ogType: options.openGraph.type } : {}),
    ...(options.twitter?.title ? { twitterTitle: options.twitter.title } : {}),
    ...(options.twitter?.description ? { twitterDescription: options.twitter.description } : {}),
    ...(options.twitter?.image ? { twitterImage: options.twitter.image } : {}),
    ...(options.twitter?.card ? { twitterCard: options.twitter.card } : {}),
    ...(options.extraMeta ?? {}),
  };

  useSeoMeta(seoMeta);

  const structuredData: any[] = [];
  const normalized = normalizeStructuredData(options.structuredData);
  structuredData.push(...normalized);

  // Auto-build BreadcrumbList from breadcrumbs option
  if (options.breadcrumbs?.length) {
    const breadcrumbSchema = buildBreadcrumbSchema({
      items: options.breadcrumbs,
      id: options.breadcrumbId,
    });
    structuredData.push(breadcrumbSchema);
  }

  // Include WebSite schema (typically homepage only)
  if (options.includeWebSite) {
    const websiteSchema = buildWebSiteSchema({
      name: options.websiteData?.name ?? DEFAULT_ORG_NAME,
      url: options.websiteData?.url ?? frontEndUrl,
      description: options.websiteData?.description ?? DEFAULT_ORG_DESCRIPTION,
      alternateName: options.websiteData?.alternateName ?? DEFAULT_ORG_ALTERNATE_NAME,
      searchUrlTemplate: options.websiteData?.searchUrlTemplate,
    });
    structuredData.push(websiteSchema);
  }

  // Include pre-fetched Organization data
  if (options.orgData) {
    const normalizedOrg = normalizeStructuredData(options.orgData);
    structuredData.push(...normalizedOrg);
  }

  // Include pre-fetched Aggregate Review data
  if (options.aggReviewData) {
    const normalizedAgg = normalizeStructuredData(options.aggReviewData);
    structuredData.push(...normalizedAgg);
  }

  // Include pre-fetched LocalBusiness data
  if (options.localBusinessData) {
    const normalizedLb = normalizeStructuredData(options.localBusinessData);
    structuredData.push(...normalizedLb);
  }

  // Include pre-fetched Reviews
  if (options.reviews?.length) {
    options.reviews.forEach((r) => {
      const normalizedReview = normalizeStructuredData(r);
      structuredData.push(...normalizedReview);
    });
  }

  const scriptEntries = structuredData.length
    ? [
        {
          key: "structured-data", // ensure updates replace prior script on client navigations
          type: "application/ld+json",
          innerHTML: JSON.stringify(structuredData.length === 1 ? structuredData[0] : structuredData),
        },
      ]
    : [];

  const host = (options.canonicalHostOverride || runtimeConfig.public?.frontEndUrl || "").replace(/\/$/, "");
  const path = options.canonicalPath || route.path;
  const normalizedPath = path.endsWith("/") ? path : `${path}/`;
  const canonicalHref = host ? `${host}${normalizedPath}` : normalizedPath;

  const linkEntries = [
    {
      rel: "canonical",
      href: canonicalHref,
    },
  ];

  useHead({
    link: linkEntries,
    script: scriptEntries,
  });

  return { seoMeta, canonicalHref, structuredData };
}

// ============================================================================
// Schema.org Structured Data Builders
// ============================================================================

// ---- BreadcrumbList ----------------------------------------------------------

/**
 * A single entry in a breadcrumb trail.
 */
export interface BreadcrumbItem {
  /** Visible breadcrumb label (e.g. "Home", "Services"). */
  name: string;
  /** Absolute URL for this breadcrumb (e.g. "https://www.spring-green.com/services/"). */
  url: string;
}

/**
 * Options for building BreadcrumbList structured data.
 */
export interface BreadcrumbSchemaOptions {
  /** Ordered breadcrumb items (first = root, last = current page). */
  items: BreadcrumbItem[];
  /** Optional JSON-LD @id for cross-referencing from other schemas. */
  id?: string;
}

/**
 * Build a BreadcrumbList schema.org JSON-LD object.
 *
 * Every `ListItem` includes the required `item`, `name`, and `position`
 * fields so that Google can produce breadcrumb rich results without warnings.
 *
 * @see https://schema.org/BreadcrumbList
 * @see https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 */
export function buildBreadcrumbSchema(options: BreadcrumbSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: options.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  if (options.id) {
    schema["@id"] = options.id;
  }

  return schema;
}

// ---- LocalBusiness ----------------------------------------------------------

export interface AreaServedSchemaObject {
  "@type": "City" | "State" | "Country";
  name: string;
  [key: string]: unknown;
}

/**
 * Options for building LocalBusiness structured data
 * @see https://schema.org/LocalBusiness
 */
export interface LocalBusinessSchemaOptions {
  /** Business name (required) */
  name: string;
  /** JSON-LD node identifier (e.g., "https://www.spring-green.com/lawn-care-locations/slug/#localbusiness") */
  id?: string;
  /** Business description */
  description?: string;
  /** URL of the business website or location page */
  url?: string;
  /** Business image URL */
  image?: string;
  /** Business telephone number (preferably in E.164 format, e.g., "+1-555-555-5555") */
  telephone?: string;
  /** Business address */
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  /** Geographic coordinates */
  geo?: {
    latitude: number;
    longitude: number;
  };
  /** Simple opening hours string (e.g., "Mo-Fr 08:00-17:00") */
  openingHours?: string | string[];
  /** Structured opening hours specification. */
  openingHoursSpecification?: Array<{
    dayOfWeek: string | string[];
    opens: string;
    closes: string;
  }>;
  /** Price range (e.g., "$", "$$", "$$$") */
  priceRange?: string;
  /** Areas served by this business */
  areaServed?:
    | string
    | string[]
    | AreaServedSchemaObject
    | AreaServedSchemaObject[];
  /** Same-as links (social profiles, etc.) */
  sameAs?: string[];
  /** Aggregate rating for the business */
  aggregateRating?: {
    ratingValue: number | string;
    reviewCount?: number | string;
    bestRating?: number | string;
    worstRating?: number | string;
  };
  /** Year the business was founded (e.g., "1977") */
  foundingDate?: string;
  /** Offer catalog(s) for this business. */
  hasOfferCatalog?: Record<string, any> | Record<string, any>[];
}

/**
 * Build a LocalBusiness schema.org JSON-LD object
 * @see https://schema.org/LocalBusiness
 */
export function buildLocalBusinessSchema(
  options: LocalBusinessSchemaOptions,
): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: options.name,
  };

  if (options.id) schema["@id"] = options.id;
  if (options.description) schema.description = options.description;
  if (options.url) schema.url = options.url;
  if (options.image) schema.image = options.image;
  if (options.telephone) schema.telephone = options.telephone;

  if (options.address) {
    const addr = options.address;
    if (
      addr.streetAddress ||
      addr.addressLocality ||
      addr.addressRegion ||
      addr.postalCode ||
      addr.addressCountry
    ) {
      schema.address = {
        "@type": "PostalAddress",
        ...(addr.streetAddress ? { streetAddress: addr.streetAddress } : {}),
        ...(addr.addressLocality ? { addressLocality: addr.addressLocality } : {}),
        ...(addr.addressRegion ? { addressRegion: addr.addressRegion } : {}),
        ...(addr.postalCode ? { postalCode: addr.postalCode } : {}),
        ...(addr.addressCountry ? { addressCountry: addr.addressCountry } : {}),
      };
    }
  }

  if (options.geo) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: options.geo.latitude,
      longitude: options.geo.longitude,
    };
  }

  if (options.openingHours) schema.openingHours = options.openingHours;

  if (options.openingHoursSpecification?.length) {
    schema.openingHoursSpecification = options.openingHoursSpecification.map((spec) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: spec.dayOfWeek,
      opens: spec.opens,
      closes: spec.closes,
    }));
  }

  if (options.priceRange) schema.priceRange = options.priceRange;

  if (options.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: options.aggregateRating.ratingValue,
      ...(options.aggregateRating.reviewCount != null
        ? { reviewCount: options.aggregateRating.reviewCount }
        : {}),
      ...(options.aggregateRating.bestRating != null
        ? { bestRating: options.aggregateRating.bestRating }
        : {}),
      ...(options.aggregateRating.worstRating != null
        ? { worstRating: options.aggregateRating.worstRating }
        : {}),
    };
  }

  if (options.foundingDate) schema.foundingDate = options.foundingDate;

  if (Array.isArray(options.areaServed)) {
    if (options.areaServed.length > 0) {
      schema.areaServed = options.areaServed.map((area) =>
        typeof area === "string" ? { "@type": "State", name: area } : area,
      );
    }
  } else if (options.areaServed) {
    schema.areaServed =
      typeof options.areaServed === "string"
        ? { "@type": "Country", name: options.areaServed }
        : options.areaServed;
  }

  if (options.sameAs?.length) schema.sameAs = options.sameAs;
  if (options.hasOfferCatalog) schema.hasOfferCatalog = options.hasOfferCatalog;

  return schema;
}

/**
 * Options for building Service structured data
 */
export interface ServiceSchemaOptions {
  name: string;
  description?: string;
  url?: string;
  image?: string;
  provider?: {
    name: string;
    url?: string;
  };
  areaServed?: string | string[];
  serviceType?: string;
}

/**
 * Build a Service schema.org JSON-LD object
 * @see https://schema.org/Service
 */
export function buildServiceSchema(options: ServiceSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: options.name,
  };

  if (options.description) {
    schema.description = options.description;
  }

  if (options.url) {
    schema.url = options.url;
  }

  if (options.image) {
    schema.image = options.image;
  }

  if (options.provider) {
    schema.provider = {
      "@type": "Organization",
      name: options.provider.name,
      ...(options.provider.url ? { url: options.provider.url } : {}),
    };
  }

  if (options.areaServed) {
    schema.areaServed = Array.isArray(options.areaServed)
      ? options.areaServed.map((area) => ({ "@type": "State", name: area }))
      : { "@type": "Country", name: options.areaServed };
  }

  if (options.serviceType) {
    schema.serviceType = options.serviceType;
  }

  return schema;
}

/**
 * Options for building ItemList structured data
 */
export interface ItemListSchemaOptions {
  name?: string;
  description?: string;
  items: Array<{
    name: string;
    url: string;
    position?: number;
  }>;
}

/**
 * Build an ItemList schema.org JSON-LD object with minimal ListItem references
 * @see https://schema.org/ItemList
 */
export function buildItemListSchema(options: ItemListSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: options.items.map((item, index) => ({
      "@type": "ListItem",
      position: item.position ?? index + 1,
      name: item.name,
      url: item.url,
    })),
  };

  if (options.name) {
    schema.name = options.name;
  }

  if (options.description) {
    schema.description = options.description;
  }

  return schema;
}

// ============================================================================
// OfferCatalog Schema Builder (for service listings)
// ============================================================================

/**
 * A single offering within an OfferCatalog. Maps to an `Offer` node with
 * `itemOffered` typed as `Service`.
 *
 * NOTE: Price fields are intentionally omitted — Spring-Green pricing is
 * property-specific and must not be indexed as a public price for SEO.
 */
export interface OfferCatalogItemOptions {
  /** Service name (required) */
  name: string;
  /** Canonical URL for the service detail page */
  url: string;
  /** Short description of the service */
  description?: string;
  /** Representative image URL for the service */
  image?: string;
  /** Category/type of service (e.g. "Lawn Care", "Pest Control") */
  serviceType?: string;
  /** Optional area(s) served for this specific service */
  areaServed?: string | string[];
}

/**
 * Options for building OfferCatalog structured data.
 * @see https://schema.org/OfferCatalog
 */
export interface OfferCatalogSchemaOptions {
  /** Catalog display name (e.g. "Lawn Care Services") */
  name: string;
  /** Optional JSON-LD @id so other nodes (e.g. LocalBusiness) can reference it */
  id?: string;
  /** URL of the catalog/listing page */
  url?: string;
  /** Short description of the catalog */
  description?: string;
  /** Provider organization for the catalog. Defaults to Spring-Green. */
  provider?: {
    name?: string;
    url?: string;
    id?: string;
  };
  /** Offered services */
  items: OfferCatalogItemOptions[];
}

/**
 * Build an OfferCatalog schema.org JSON-LD object.
 *
 * Each item is emitted as an `Offer` with `itemOffered` typed as `Service`.
 * No price/priceCurrency fields are included — pricing is individualized per
 * property and must not be indexed.
 *
 * @see https://schema.org/OfferCatalog
 * @see https://schema.org/Offer
 * @see https://schema.org/Service
 */
export function buildOfferCatalogSchema(options: OfferCatalogSchemaOptions): Record<string, any> {
  const providerName = options.provider?.name ?? DEFAULT_ORG_NAME;
  const providerUrl = options.provider?.url ?? DEFAULT_SITE_URL;
  const provider: Record<string, any> = options.provider?.id
    ? { "@id": options.provider.id }
    : {
        "@type": "Organization",
        name: providerName,
        url: providerUrl,
      };

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: options.name,
    itemListElement: options.items.map((item) => {
      const service: Record<string, any> = {
        "@type": "Service",
        name: item.name,
        url: item.url,
        provider,
      };
      if (item.description) service.description = item.description;
      if (item.image) service.image = item.image;
      if (item.serviceType) service.serviceType = item.serviceType;
      if (item.areaServed) {
        service.areaServed = Array.isArray(item.areaServed)
          ? item.areaServed.map((area) => ({ "@type": "State", name: area }))
          : { "@type": "Country", name: item.areaServed };
      }
      return {
        "@type": "Offer",
        itemOffered: service,
      };
    }),
  };

  if (options.id) schema["@id"] = options.id;
  if (options.url) schema.url = options.url;
  if (options.description) schema.description = options.description;

  return schema;
}

/**
 * Options for building FAQPage structured data
 */
export interface FaqSchemaOptions {
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

/**
 * Build a FAQPage schema.org JSON-LD object
 * @see https://schema.org/FAQPage
 */
export function buildFaqSchema(options: FaqSchemaOptions): Record<string, any> | null {
  const validFaqs = options.faqs.filter((faq) => faq.question && faq.answer);

  if (!validFaqs.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: validFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/**
 * Options for building WebSite structured data
 */
export interface WebSiteSchemaOptions {
  name: string;
  url: string;
  description?: string;
  searchUrlTemplate?: string;
  alternateName?: string;
}

/**
 * Build a WebSite schema.org JSON-LD object with optional SearchAction for sitelinks search box
 * @see https://schema.org/WebSite
 * @see https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
 */
export function buildWebSiteSchema(options: WebSiteSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: options.name,
    url: options.url,
  };

  if (options.description) {
    schema.description = options.description;
  }

  if (options.alternateName) {
    schema.alternateName = options.alternateName;
  }

  // Add SearchAction for sitelinks search box if search URL template is provided
  if (options.searchUrlTemplate) {
    schema.potentialAction = {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: options.searchUrlTemplate,
      },
      "query-input": "required name=search_term_string",
    };
  }

  return schema;
}

// ============================================================================
// Article / BlogPosting Schema Builders
// ============================================================================

/**
 * Options for building Article or BlogPosting structured data.
 * All date fields should be in ISO 8601 format (e.g., "2025-01-22" or "2025-01-22T10:00:00Z").
 */
export interface ArticleSchemaOptions {
  /** Headline of the article (required by Google) */
  headline: string;
  /** Author of the content. Defaults to Spring-Green organization if not provided. */
  author?: {
    name: string;
    url?: string;
    type?: "Person" | "Organization";
  };
  /** Date of first publication in ISO 8601 format */
  datePublished?: string;
  /** Most recent modification date in ISO 8601 format */
  dateModified?: string;
  /** Primary image URL for the article */
  image?: string | string[];
  /** Publisher organization. Defaults to Spring-Green if not provided. */
  publisher?: {
    name: string;
    logo?: string;
    url?: string;
  };
  /** Short description/summary of the article */
  description?: string;
  /** Section/category of the article (e.g., "Lawn Care", "Pest Control") */
  articleSection?: string;
  /** Canonical URL of the article page */
  url?: string;
  /** Canonical URL for mainEntityOfPage (defaults to url if not provided) */
  mainEntityOfPage?: string;
  /** Word count of the article body */
  wordCount?: number;
  /** Keywords associated with the article */
  keywords?: string | string[];
}

/**
 * Internal helper to build Article or BlogPosting schema
 */
function buildArticleSchemaInternal(
  options: ArticleSchemaOptions,
  type: "Article" | "BlogPosting"
): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": type,
    headline: options.headline,
  };

  // Author - defaults to Spring-Green organization
  const authorType = options.author?.type ?? "Organization";
  const authorName = options.author?.name ?? DEFAULT_ORG_NAME;
  schema.author = {
    "@type": authorType,
    name: authorName,
    ...(options.author?.url ? { url: options.author.url } : authorType === "Organization" ? { url: DEFAULT_SITE_URL } : {}),
  };

  // Publisher - defaults to Spring-Green organization
  schema.publisher = {
    "@type": "Organization",
    name: options.publisher?.name ?? DEFAULT_ORG_NAME,
    ...(options.publisher?.url ? { url: options.publisher.url } : { url: DEFAULT_SITE_URL }),
    ...(options.publisher?.logo ? { logo: { "@type": "ImageObject", url: options.publisher.logo } } : {}),
  };

  if (options.datePublished) {
    schema.datePublished = options.datePublished;
  }

  if (options.dateModified) {
    schema.dateModified = options.dateModified;
  }

  if (options.image) {
    schema.image = options.image;
  }

  if (options.description) {
    schema.description = options.description;
  }

  if (options.articleSection) {
    schema.articleSection = options.articleSection;
  }

  if (options.url) {
    schema.url = options.url;
  }

  // mainEntityOfPage - use provided value, or fall back to url
  if (options.mainEntityOfPage || options.url) {
    schema.mainEntityOfPage = {
      "@type": "WebPage",
      "@id": options.mainEntityOfPage ?? options.url,
    };
  }

  if (options.wordCount) {
    schema.wordCount = options.wordCount;
  }

  if (options.keywords) {
    schema.keywords = Array.isArray(options.keywords) ? options.keywords.join(", ") : options.keywords;
  }

  return schema;
}

/**
 * Build an Article schema.org JSON-LD object
 * @see https://schema.org/Article
 * @see https://developers.google.com/search/docs/appearance/structured-data/article
 */
export function buildArticleSchema(options: ArticleSchemaOptions): Record<string, any> {
  return buildArticleSchemaInternal(options, "Article");
}

/**
 * Build a BlogPosting schema.org JSON-LD object (extends Article)
 * @see https://schema.org/BlogPosting
 * @see https://developers.google.com/search/docs/appearance/structured-data/article
 */
export function buildBlogPostingSchema(options: ArticleSchemaOptions): Record<string, any> {
  return buildArticleSchemaInternal(options, "BlogPosting");
}

// ============================================================================
// DefinedTerm / DefinedTermSet Schema Builders (for Glossary pages)
// ============================================================================

/**
 * Options for building DefinedTerm structured data
 */
export interface DefinedTermSchemaOptions {
  /** The term being defined (required) */
  name: string;
  /** The definition of the term (required) */
  description: string;
  /** URL of the term page */
  url?: string;
  /** Alpha-numeric code for the term (optional) */
  termCode?: string;
  /** The term set containing this term */
  inDefinedTermSet?: {
    name: string;
    url?: string;
  };
}

/**
 * Build a DefinedTerm schema.org JSON-LD object
 * @see https://schema.org/DefinedTerm
 */
export function buildDefinedTermSchema(options: DefinedTermSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: options.name,
    description: options.description,
  };

  if (options.url) {
    schema.url = options.url;
  }

  if (options.termCode) {
    schema.termCode = options.termCode;
  }

  if (options.inDefinedTermSet) {
    schema.inDefinedTermSet = {
      "@type": "DefinedTermSet",
      name: options.inDefinedTermSet.name,
      ...(options.inDefinedTermSet.url ? { url: options.inDefinedTermSet.url } : {}),
    };
  }

  return schema;
}

/**
 * Options for building DefinedTermSet structured data
 */
export interface DefinedTermSetSchemaOptions {
  /** Name of the term set (e.g., "Lawn Care Glossary") */
  name: string;
  /** Description of the term set */
  description?: string;
  /** URL of the glossary index page */
  url?: string;
  /** Array of terms in the set (for glossary index pages) */
  terms?: Array<{
    name: string;
    description: string;
    url?: string;
  }>;
}

/**
 * Build a DefinedTermSet schema.org JSON-LD object
 * @see https://schema.org/DefinedTermSet
 */
export function buildDefinedTermSetSchema(options: DefinedTermSetSchemaOptions): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: options.name,
  };

  if (options.description) {
    schema.description = options.description;
  }

  if (options.url) {
    schema.url = options.url;
  }

  // Include hasDefinedTerm array if terms are provided
  if (options.terms?.length) {
    schema.hasDefinedTerm = options.terms.map((term) => ({
      "@type": "DefinedTerm",
      name: term.name,
      description: term.description,
      ...(term.url ? { url: term.url } : {}),
    }));
  }

  return schema;
}

// ============================================================================
// AboutPage Schema Builder
// ============================================================================

/**
 * Options for building AboutPage structured data
 */
export interface AboutPageSchemaOptions {
  /** Page name / title (required) */
  name: string;
  /** Short description of the about page */
  description?: string;
  /** Canonical URL of the about page (without trailing fragment) */
  url: string;
  /** @id for the WebSite this page is part of (e.g. "https://www.spring-green.com/#website") */
  websiteId?: string;
  /** @id for the Organization this page is about (e.g. "https://www.spring-green.com/#organization") */
  organizationId?: string;
  /** @id for a BreadcrumbList associated with this page */
  breadcrumbId?: string;
}

/**
 * Build an AboutPage schema.org JSON-LD object.
 * @see https://schema.org/AboutPage
 */
export function buildAboutPageSchema(options: AboutPageSchemaOptions): Record<string, any> {
  const pageUrl = options.url.replace(/\/$/, ""); // strip trailing slash for fragment IDs

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${pageUrl}/#aboutpage`,
    url: pageUrl,
    name: options.name,
  };

  if (options.description) {
    schema.description = options.description;
  }

  if (options.websiteId) {
    schema.isPartOf = { "@id": options.websiteId };
  }

  if (options.organizationId) {
    schema.about = { "@id": options.organizationId };
  }

  if (options.breadcrumbId) {
    schema.breadcrumb = { "@id": options.breadcrumbId };
  }

  return schema;
}
